"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState } from "react";

import type { SavedCity } from "@/components/choghadiya/ChoghadiyaCitySelector";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";
import { DEFAULT_SHUBH_CITY } from "@/app/shubh-muhurat/shubh-muhurat-config";

import type { DateFinderProps } from "./_date-finder.model";
import {
  DateRows,
  FinderControls,
  ShellCityButton,
  checkButtonId,
  type DateRowsProps,
} from "./MuhuratDateFinderView";

// The /muhurat/<event>/ date finder boundary (the #2460 pattern): the server
// page imports only this module, and it owns the dynamic() edges to the
// island (MuhuratDateFinder.tsx: the live city search and the decorated date
// list, one async chunk). It is server-rendered, so the controls and the
// compact list are in the HTML; the Tara rule, the city search, the engine
// client and the InauspiciousSlots cards load on the visitor's own press.
//
// Activation is element-scoped (the nakshatra finder's rule, 2026-10-05): a
// reader scrolling the page, or the deploy smoke's body pointerdown, loads
// nothing. A press or focus on the birth-star select, the city button or a
// date's "Check in" button does.
//
// Nothing the visitor does in the shell is lost when the island arrives:
// - the birth-star select is rendered here for the page's lifetime (never
//   swapped), and its value is handed to the island;
// - a "Check in" press opens that date's reserved panel here at once; the
//   island mounts with it open, runs the check and takes focus back to the
//   pressed button;
// - a city press mounts the island with its city search open.
// translate="no" covers the controls and the list, which re-render on every
// choice (tests/translate-safe-islands.test.ts).

const loadIsland = () => import("./MuhuratDateFinder");

/** The list's chunk-loading frame reads the shell's live props from context
 * (dynamic()'s `loading` receives none). */
const RowsContext = createContext<DateRowsProps | null>(null);

function LoadingRows() {
  const rows = useContext(RowsContext);
  return rows ? <DateRows {...rows} /> : null;
}

const FinderDates = dynamic(() => loadIsland().then((island) => island.FinderDates), {
  ssr: false,
  loading: () => <LoadingRows />,
});

const FinderCity = dynamic(() => loadIsland().then((island) => island.FinderCity), {
  ssr: false,
  loading: () => <ShellCityButton name={DEFAULT_SHUBH_CITY.name} />,
});

export default function MuhuratDateFinderLazy(finder: DateFinderProps) {
  const { ready, activate } = useDeferredToolActivation({ scope: "element" });
  const [janma, setJanma] = useState<number | null>(null);
  const [city, setCity] = useState<SavedCity>(DEFAULT_SHUBH_CITY);
  const [openIso, setOpenIso] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [openCity, setOpenCity] = useState(false);

  const onCheck = (iso: string) => {
    setOpenIso((current) => (current === iso ? null : iso));
    setFocusId(checkButtonId(iso));
    activate();
  };
  const rows: DateRowsProps = { finder, cityName: city.name, openIso, onCheck };

  return (
    <div
      translate="no"
      data-muhurat-date-finder=""
      className="mt-6 rounded-lg border border-border-default bg-surface-raised p-4 sm:p-5"
    >
      <FinderControls
        options={finder.janmaOptions}
        janma={janma}
        onJanmaChange={setJanma}
        onJanmaIntent={activate}
        cityName={city.name}
        cityControl={
          ready ? (
            <FinderCity city={city} onCitySelect={setCity} openOnMount={openCity} />
          ) : (
            <ShellCityButton
              name={city.name}
              onPress={() => {
                setOpenCity(true);
                activate();
              }}
            />
          )
        }
      />
      <RowsContext.Provider value={rows}>
        {ready ? (
          <FinderDates {...rows} janma={janma} city={city} focusId={focusId} />
        ) : (
          <DateRows {...rows} />
        )}
      </RowsContext.Provider>
    </div>
  );
}
